import { Syringe, Droplets, ArrowRightLeft, CalendarDays, ClipboardList } from 'lucide-react';

/*
 * MD Anderson Acute Pain rotation. Built from the Blackboard Acute Pain Orientation guide
 * and the equianalgesic conversion table. See docs/blackboard-md-anderson.md.
 */

const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const PAIN = BB_DOC('_257852_1');

export const mdaAcutePain = {
  accent: '#da322a',
  icon: Syringe,
  tagline: 'Consults, blocks, epidurals, day and night shifts',
  notesKey: 'notes:md-anderson:acute-pain',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'epidural', title: 'Epidurals & Catheters', subtitle: 'Assessment, hypotension, anticoagulation', icon: Droplets, action: 'guide:epidural', featured: true },
      { id: 'opioids', title: 'Opioid Conversion', subtitle: 'Equianalgesic doses and fentanyl patches', icon: ArrowRightLeft, action: 'guide:opioids' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'day', title: 'Your Shifts', subtitle: 'Day, night, weekend', icon: CalendarDays, action: 'guide:day' },
      { id: 'start', title: 'Getting Started', subtitle: 'Badge, orientation, the team room, what to ask for', icon: ClipboardList, action: 'guide:start' },
    ] },
  ],

  contacts: {
    storageKey: 'mda-pain-favorites',
    groups: [
      { id: 'pain', title: 'Acute pain service', open: true, items: [
        { label: 'APMS resident phone', phone: '8325388011', note: 'Call it in the morning to find your co-resident for sign-out' },
      ] },
    ],
  },

  locations: {
    groups: [
      { id: 'pain', title: 'Acute pain', items: [
        { name: 'Acute Pain Service team room', floor: '7th floor', where: 'B7.4523', note: 'D elevators to 7, turn right, left up the ramp of the first hallway, through the double doors, on the right. Badge opens it' },
      ] },
    ],
  },

  documents: {
    storageKey: 'mda-pain-pinned-docs',
    groups: [
      { id: 'pain', title: 'Acute pain', items: [
        { title: 'Acute pain orientation', subtitle: 'Blackboard', url: PAIN },
        { title: 'Anticoagulation and neuraxial', subtitle: 'University of Washington guideline. Blackboard', url: BB_DOC('_257856_1') },
        { title: 'Equianalgesic conversion table', subtitle: 'Blackboard', url: BB_DOC('_257858_1') },
        { title: 'ASRA Coags app', subtitle: 'App Store', url: 'https://apps.apple.com/us/app/asra-coags/id1021516934' },
        { title: 'Block Buddy', subtitle: 'App Store', url: 'https://apps.apple.com/us/app/block-buddy/id1228006604' },
      ] },
    ],
  },

  guides: {
    epidural: {
      title: 'Epidurals & Catheters',
      sections: [
        { title: 'Assessment', blocks: [{ type: 'bullets', items: [
          'Sensory and motor exam, vital signs, pain score and level',
          'Insertion site where it is taped',
          'Side effects: nausea, itching',
          'Aspirate the catheter for blood or CSF before injecting anything',
          'Brief progress note after every visit, with the pain score and level',
        ] }] },
        { title: 'Hypotension', blocks: [{ type: 'bullets', items: [
          { text: 'You are a consult service: no fluid or pressor orders', warn: true },
          'Assess for symptoms. Pause the epidural or lower the rate or the local anesthetic concentration',
          'Ask the bedside nurse to page the primary team for fluids',
        ] }] },
        { title: 'Anticoagulation', blocks: [{ type: 'bullets', items: [
          'Check the MAR for the timing of the last dose before pulling back or removing an epidural or adjusting a catheter',
          'Anticoagulation held before any catheter comes out',
          'ASRA Coags app for the current intervals',
        ] }] },
        { title: 'Other rules', blocks: [{ type: 'bullets', items: [
          'NSAIDs and celecoxib need the primary team’s approval first',
          'Many patients already belong to chronic pain or supportive care. They go back to those services once the acute pain is handled',
        ] }] },
      ],
      sources: [{ title: 'MDA Acute Pain Rotation Orientation', href: PAIN, detail: 'Blackboard, sign-in required' }],
    },

    opioids: {
      title: 'Opioid Conversion',
      sections: [
        { title: 'Equianalgesic doses', blocks: [{ type: 'table', cols: ['Opioid', 'Oral', 'IV or SC', 'IV to PO', 'PO to oral morphine'], rows: [
          ['Morphine', '3 mg', '1 mg', '×3', '×1'],
          ['Hydromorphone', '0.75 mg', '0.15 mg', '×5', '×4'],
          ['Oxycodone', '2 mg', '', '', '×1.5'],
          ['Hydrocodone', '3 mg', '', '', '×1'],
          ['Oxymorphone', '1 mg', '0.1 mg', '×10', '×3'],
          ['Codeine', '20 mg', '10 mg', '×2', '×0.15'],
          ['Tramadol', '12 mg', '10 mg', '×1.2', '×0.25'],
          ['Meperidine', '30 mg', '10 mg', '×3', '×0.1'],
          ['Fentanyl', '', '60 mcg', '', ''],
        ] }] },
        { title: 'Fentanyl patch', blocks: [{ type: 'table', cols: ['Oral morphine, mg/day', 'Patch, mcg/h'], rows: [
          ['30–90', '25'],
          ['91–150', '50'],
          ['151–210', '75'],
          ['211–270', '100'],
          ['Each further 60', 'Add 25'],
        ] }, { type: 'note', text: '2 mg oral morphine a day is about 1 mcg/h transdermal fentanyl.' }] },
      ],
      sources: [{ title: 'Equianalgesic opioid dose conversion', href: BB_DOC('_257858_1'), detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Your Shifts',
      segments: [
        { id: 'day', label: 'Day', sections: [
          { title: '07:00–19:00 or 07:00–15:00', blocks: [{ type: 'bullets', items: [
            'In at 06:45 to help the night resident set up the morning blocks, then take handoff',
            'Split the morning blocks and epidurals with the regional fellow',
            'Consult pages: see the patient, write the consult note, cosign with the attending',
            'Ask the NPs and RNs which follow-ups on the APM list they want seen. Follow-up notes are cosigned',
            'Follow up every block and epidural after surgery with a brief note',
            'Anticoagulation held before any catheter is pulled or pulled back',
            'Your attending may ask for an informal journal club during the month',
          ] }] },
        ] },
        { id: 'night', label: 'Night', sections: [
          { title: '19:00–07:00', blocks: [{ type: 'bullets', items: [
            'Handoff from the day team, then round on the list. Brief note for any intervention; no cosign needed',
            'Overnight consults: staff by text or phone with the on-call attending, write the consult note, cosign',
            'Before morning handoff: brief round, collect the 24-hour PCA dose from each pump',
            'Set up the morning: TAP block and ultrasound orders, text the anesthesia techs about impending blocks, set up so APM finishes before first case',
          ] }] },
        ] },
        { id: 'weekend', label: 'Weekend', sections: [
          { title: 'From 07:00', blocks: [{ type: 'bullets', items: [
            'Pre-round on the existing patients',
            'Round with the attending. Follow-up notes are cosigned',
            'New consults seen and staffed, with a consult note',
          ] }] },
        ] },
      ],
      sources: [{ title: 'MDA Acute Pain Rotation Orientation', href: PAIN, detail: 'Blackboard, sign-in required' }],
    },

    start: {
      title: 'Getting Started',
      sections: [
        { title: 'Before the rotation', blocks: [{ type: 'bullets', items: [
          'Badge paperwork from the MDA administrator’s email. Pick the badge up from HR before day one',
          'First part of the training modules. Read the acute pain articles on Blackboard',
          'Vacation requests two months ahead',
        ] }] },
        { title: 'Day one', blocks: [{ type: 'bullets', items: [
          'Orientation is required even post-call. Modules, scrub machine access, Epic access',
          'Then the team room, B7.4523',
          'Save the APMS resident phone number',
        ] }] },
        { title: 'Ask an NP or RN to show you', blocks: [{ type: 'bullets', items: [
          'Where the block cart is',
          'Pyxis access',
          'Epidural and PCA order sets',
          'Progress note and consult templates, and the handoff list',
          'How to set up for blocks in the morning. This matters most',
        ] }] },
      ],
      sources: [{ title: 'MDA Acute Pain Rotation Orientation', href: PAIN, detail: 'Blackboard, sign-in required' }],
    },
  },
};
